"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useSearchParams } from "next/navigation";

import Loading from "@/app/loading";
import FormLabel from "@/components/form/form-label";
import MultiSelect from "@/components/form/multi-select";
import Pagination from "@/components/form/pagination";
import RCRange from "@/components/form/rc-slider-range";
import { RowsDropdown } from "@/components/form/rows";
import FetchError from "@/components/ui/fetch-error";

import VariableSearchTable from "./variable-search-table";

import api from "@/lib/api";

type HrdSearchHomeProps = {
  studies: { value: number; label: string }[];
  years: [number, number];
};

type VariableSearchDataProps = {
  count: number;
  next: string;
  previous: string;
  results: {
    value: number;
    label: string;
    dataset_title: string;
    module_title: string;
    description: string;
  }[];
};

type FormValues = {
  keyword?: string;
  study?: number[];
  years?: number[];
  page_size?: number;
  page?: number;
};

const getVariableSearchData = async (
  page: string,
  page_size: string,
  study: number[] = [],
  keyword: string = "",
  survey_type: number[] = [],
  yearStart: string = "",
  yearEnd: string = "",
) => {
  const params = new URLSearchParams();
  params.append("site", "6"); // HCAP site ID is 6
  params.append("keyword", keyword);
  study.forEach((item) => params.append("studies", item.toString()));
  survey_type.forEach((item) => params.append("sites", item.toString()));
  params.append("years", `${yearStart},${yearEnd}`);
  params.append("page", page);
  params.append("page_size", page_size);

  try {
    const response = await api.get<VariableSearchDataProps>(`/hrd/search/data/?${params.toString()}`);
    return response;
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export default function VariableSearchHome() {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [tableData, setTableData] = useState<VariableSearchDataProps | undefined>();
  const [loading, setLoading] = useState(false);

  const searchParams = useSearchParams();
  const initialKeyword = searchParams.get("keyword") || "";

  const { handleSubmit, control, reset, register, watch, setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      years: [],
      keyword: initialKeyword,
      study: [],
      page_size: 10,
    },
  });

  const page_size = watch("page_size") || 10;
  const selectedStudy = watch("study");
  const keyword = watch("keyword");

  const params = new URLSearchParams({ site_name: "hcap" });

  const { isPending, error, data } = useQuery<HrdSearchHomeProps, Error>({
    queryKey: ["hrd-hcap-search-home"],
    queryFn: async () => await api.get(`/hrd/search/home/?${params.toString()}`),
  });

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    try {
      setLoading(true);
      setTableData(undefined);

      const [startYear, endYear] =
        formData.years && formData.years.length === 2 ? formData.years : (data?.years ?? [1990, 2010]);

      const hcapSiteValue = 6;

      // Only use HCAP site in query
      const surveyTypes = hcapSiteValue ? [hcapSiteValue] : [];

      const response = await getVariableSearchData(
        formData.page?.toString() || "1",
        formData.page_size?.toString() || "10",
        formData.study || [],
        formData.keyword || "",
        surveyTypes,
        startYear.toString(),
        endYear.toString(),
      );

      setTableData(response);
    } catch (error) {
      console.error("Error fetching table data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial form load and page changes
  useEffect(() => {
    if (data) {
      setValue("years", data.years);
      const formValues = getValues();
      onSubmit({
        ...formValues,
        years: formValues.years?.length === 2 ? formValues.years : data.years,
        page: currentPage,
        page_size,
        keyword,
        study: selectedStudy,
      });
    }
  }, [data, currentPage]);

  const resetForm = () => {
    if (data) {
      const resetValues = {
        page: 1,
        page_size: 10,
        keyword: "",
        study: [],
        years: data.years,
      };
      reset(resetValues);
      setCurrentPage(1);
      onSubmit(resetValues);
    }
  };

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row g-3 mb-4">
        <div className="col-md-4 col-sm-6 mt-2">
          <FormLabel label="Keyword" />
          <input type="text" className="form-control" placeholder="Keyword" {...register("keyword")} />
        </div>

        <div className="col-md-4 col-sm-6 mt-2">
          <MultiSelect options={data.studies} isPending={isPending} control={control} name="study" label="Study" />
        </div>

        <div className="col-md-4 col-sm-6 mt-2">
          <RCRange
            label="Year"
            min={data.years[0]}
            max={data.years[1]}
            value={watch("years") ?? []}
            onChange={(value) => {
              const years = Array.isArray(value) ? value : [value];
              setValue("years", years);
            }}
          />
        </div>

        <div className="mt-4">
          <div className="col d-flex justify-content-end align-items-end">
            <RowsDropdown name="page_size" control={control} onSubmit={handleSubmit(onSubmit)} />
            <button
              type="button"
              className="btn btn-outline-secondary"
              style={{ marginRight: "1rem" }}
              onClick={resetForm}
            >
              RESET
            </button>
            <button type="submit" className="btn g2-btn-blue">
              SEARCH
            </button>
          </div>
        </div>
      </form>

      {loading && !tableData ? <Loading /> : <VariableSearchTable data={tableData?.results} />}

      {tableData && tableData.count > 0 && (
        <Pagination
          totalCount={tableData.count}
          page_size={page_size}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          previous={tableData.previous}
          next={tableData.next}
        />
      )}
    </>
  );
}
